import { IonAlert } from '@ionic/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import WebViewResource from '../../../../../../../services/resources/WebViewResource';
import {
  AppWebViewMessageTypes,
  PushNotificationStatus,
  REDUX_STORE_SLICES,
} from '../../../../../../../services/exports/Constants';
import useStore from '../../../../../../../hooks/store/useStore';
import { Tabs } from '../../../../../exports/Enums';
import ReduxActions from '../../../../../../../store/ReduxActions';
import { actionCreators } from '../../../../../../../store/actions';
import collect from 'collect.js';
import moment from 'moment';
import { IPushNotificationPopUp } from '../../../../../../../store/types';
import useEffectOnce from '../../../../../common/hooks/useEffectOnce';
import useMobileApp from '../../../../../../../hooks/mobile-app/useMobileApp';
import useLoading from '../../../../../../../hooks/global/useLoading';

const PushNotificationAlert = (): JSX.Element => {
  const { t } = useTranslation(null, {
    keyPrefix: 'Modules:Mobile:Pages:Tabs:Home:Components:PushNotifications:PushNotificationAlert',
  });
  const { loading } = useLoading();
  const { pushNotificationPopup, sessions, pushNotificationsStatus } = useStore<REDUX_STORE_SLICES.MOBILE_APP>(
    REDUX_STORE_SLICES.MOBILE_APP,
  );
  const tab = Tabs.Home;
  const { pushNotificationPopUpOpen, setPushNotificationPopUpOpen } = useMobileApp();

  const shouldShowPushPrompt = useMemo(() => {
    if (!pushNotificationsStatus || loading) {
      return false;
    }

    if (pushNotificationsStatus && pushNotificationsStatus !== PushNotificationStatus.Undetermined) {
      return false;
    }

    const popup = collect(pushNotificationPopup).get(tab) as IPushNotificationPopUp;

    if (!popup || !popup.askedAt) return true;

    const askedAt = collect(popup.askedAt);
    if (askedAt.count() >= 3) return false;
    if (askedAt.count() === 0) return true;

    const lastAsked = moment(askedAt.last());
    const now = moment();
    const isAfter7Days = now.diff(lastAsked, 'days') >= 7;

    const newSessions = collect(sessions).filter((session) => moment(session).isAfter(lastAsked));

    if (newSessions.isNotEmpty() || isAfter7Days) {
      return true;
    }

    return false;
  }, [pushNotificationPopup, sessions, pushNotificationsStatus, loading]);

  useEffectOnce(() => {
    setPushNotificationPopUpOpen(shouldShowPushPrompt);
  }, shouldShowPushPrompt);

  const onClose = (): void => {
    setPushNotificationPopUpOpen(false);
    ReduxActions.dispatch(
      actionCreators[REDUX_STORE_SLICES.MOBILE_APP].presentedPushNotificationPopup({
        tab,
      }),
    );
  };

  const onIonAlertDidDismiss = (event) => {
    const role = event.detail?.role;

    if (role === 'confirm') {
      WebViewResource.sendMessage(AppWebViewMessageTypes.RequestPushNotification, null);
    }

    onClose();
  };

  return (
    <IonAlert
      isOpen={pushNotificationPopUpOpen}
      header={t('title')}
      message={t('description')}
      onIonAlertDidDismiss={onIonAlertDidDismiss}
      buttons={[
        {
          text: t('buttons.cancel'),
          role: 'cancel',
        },
        {
          text: t('buttons.confirm'),
          role: 'confirm',
          cssClass: 'alert-button-confirm',
        },
      ]}
    />
  );
};

export default PushNotificationAlert;
